import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Slider, Tooltip, Typography } from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from "@mui/icons-material";
import { Document, Page } from "react-pdf";

const normalizePage = (page, pageCount) =>
  Math.min(Math.max(Number(page) || 1, 1), Math.max(pageCount || 1, 1));

const TableTagPdfPreview = ({ file, initialPage = 1, displayRotation }) => {
  const [pageNumber, setPageNumber] = useState(Math.max(initialPage || 1, 1));
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(0.85);
  const previewRef = useRef(null);

  useEffect(() => {
    const guard = (e) => {
      if (
        e.type === "keydown" &&
        !((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "c")
      ) {
        return;
      }
      const selection = window.getSelection();
      if (
        selection &&
        !selection.isCollapsed &&
        previewRef.current?.contains(selection.anchorNode)
      ) {
        e.stopPropagation();
      }
    };

    window.addEventListener("keydown", guard, true);
    window.addEventListener("copy", guard, true);
    return () => {
      window.removeEventListener("keydown", guard, true);
      window.removeEventListener("copy", guard, true);
    };
  }, []);

  useEffect(() => {
    setPageNumber(normalizePage(initialPage, numPages));
  }, [file, initialPage, numPages]);

  const handleDocumentLoadSuccess = ({ numPages: loadedPageCount }) => {
    setNumPages(loadedPageCount);
    setPageNumber((currentPage) => normalizePage(currentPage, loadedPageCount));
  };

  const movePage = (offset) => {
    setPageNumber((currentPage) =>
      normalizePage(currentPage + offset, numPages),
    );
  };

  const handleScaleChange = (_, newValue) => {
    setScale(newValue);
  };

  if (!file) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          p: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          PDF reference is unavailable.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1,
          backgroundColor: "#fff",
          borderBottom: "1px solid #dfe3e8",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            PDF reference
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2">Zoom:</Typography>
            <Tooltip title="Zoom Out">
              <IconButton
                size="small"
                disabled={scale <= 0.5}
                onClick={() =>
                  handleScaleChange(null, Math.max(scale - 0.1, 0.5))
                }
                aria-label="Zoom out PDF preview"
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Slider
              value={scale}
              onChange={handleScaleChange}
              min={0.5}
              max={2.0}
              step={0.1}
              sx={{ width: 100 }}
              aria-label="PDF preview zoom"
            />
            <Tooltip title="Zoom In">
              <IconButton
                size="small"
                disabled={scale >= 2.0}
                onClick={() =>
                  handleScaleChange(null, Math.min(scale + 0.1, 2.0))
                }
                aria-label="Zoom in PDF preview"
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2">{Math.round(scale * 100)}%</Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Previous page">
              <span>
                <IconButton
                  size="small"
                  disabled={pageNumber <= 1}
                  onClick={() => movePage(-1)}
                  aria-label="Previous PDF page"
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{ minWidth: 76, textAlign: "center", fontWeight: 600 }}
            >
              Page {pageNumber} of {numPages || "—"}
            </Typography>
            <Tooltip title="Next page">
              <span>
                <IconButton
                  size="small"
                  disabled={!numPages || pageNumber >= numPages}
                  onClick={() => movePage(1)}
                  aria-label="Next PDF page"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Box
        ref={previewRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          p: 2,
          "& .react-pdf__Document": {
            width: "max-content",
            minWidth: "100%",
          },
          "& .react-pdf__Page": {
            width: "max-content",
            mx: "auto",
            boxShadow: "0 3px 14px rgba(15, 23, 42, 0.2)",
          },
        }}
      >
        <Document
          file={file}
          onLoadSuccess={handleDocumentLoadSuccess}
          loading={
            <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Loading PDF reference…
              </Typography>
            </Box>
          }
          error={
            <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
              <Typography variant="body2" color="error">
                Unable to load the PDF reference.
              </Typography>
            </Box>
          }
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            rotate={displayRotation}
            renderTextLayer={true}
            renderAnnotationLayer={false}
          />
        </Document>
      </Box>
    </Box>
  );
};

export default TableTagPdfPreview;
